import { useEffect, useMemo, useState } from 'react';
import { MainLayout } from '@/components/layout/MainLayout';
import { Button } from '@/components/ui/button';
import { Button3D } from '@/components/ui/button-3d';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useAuth } from '@/contexts/AuthContext';
import { useCompany } from '@/contexts/useCompany';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { useNetworkStatus } from '@/hooks/useNetworkStatus';
import { supabase } from '@/integrations/supabase/client';
import type { Quote, QuoteStatus } from '@/types/database';
import {
  FileText,
  Search,
  Loader2,
  Eye,
  Clock,
  FileSpreadsheet,
  Plus,
  ArrowUpDown,
  SlidersHorizontal,
  Check,
} from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { toast } from 'sonner';
import { useBulkSelection } from '@/hooks/useBulkSelection';
import { useXlsxExport } from '@/hooks/useXlsxExport';
import { buildQuotesXlsx, type QuoteItemRow } from '@/lib/xlsxTemplates';
import { BulkActionBar } from '@/components/bulk-actions/BulkActionBar';
import { BulkDeleteDialog } from '@/components/bulk-actions/BulkDeleteDialog';
import { BulkDuplicateDialog } from '@/components/bulk-actions/BulkDuplicateDialog';
import { PremiumModal } from '@/components/modals/PremiumModal';
import { cacheQuotes, getCachedQuotes } from '@/lib/offlineDataCache';
import { useRole } from '@/hooks/useRole';
import { isSaleDocument } from '@/lib/quickSale';
import { StockReturnOnDeleteNotice } from '@/components/stock/StockReturnOnDeleteNotice';
import { useDocumentDeletionReturn } from '@/hooks/useDocumentDeletionReturn';
import { runDocumentDeletion, type DocumentDeletionOutcome } from '@/lib/documentDeletion';
import { billableReleaseMessage, deleteQuotesCascade, type QuoteDeletionCounts } from '@/lib/quoteDeletion';
import { plural } from '@/lib/plural';
import { buildQuoteItemInsert } from '@/lib/quoteItems';
import { buildQuoteWarrantyColumns, quoteWarrantyFromRow } from '@/lib/quoteWarranty';
import { buildQuoteHeadingColumns, quoteHeadingFromRow } from '@/lib/quoteHeading';
import { buildQuoteExtraFieldsColumns, quoteExtraFieldsFromRow } from '@/lib/quoteExtraFields';
import { cn } from '@/lib/utils';
import { CONTENT_CARD } from '@/components/ui/field';
import { heroTotalSizeClass } from '@/lib/quoteHero';
import { summarizeQuoteList, quoteListTotalLabel } from '@/lib/quotesListSummary';
import { quoteMatchesSearch } from '@/lib/quoteSearch';
import { useQuoteItemNames } from '@/hooks/useQuoteItemNames';
import { isDeadStatusFilter } from '@/lib/quotesListSummary';
import {
  DEFAULT_QUOTE_SORT,
  EMPTY_VALUE_RANGE,
  QUOTE_SORT_LABELS,
  QUOTE_SORT_ORDER,
  countActiveModalFilters,
  countQuotesByStatus,
  matchesPeriod,
  matchesValueRange,
  sortQuotesBy,
  type QuoteSortOption,
  type QuoteStatusFilter,
  type QuoteValueRange,
} from '@/lib/quoteListFilters';
import { resolvePeriodPreset, type ReportPeriodPreset } from '@/lib/reportPeriod';
import {
  QuoteFiltersDialog,
  FILTER_CHIP,
  FILTER_CHIP_ON,
  FILTER_CHIP_OFF,
} from '@/components/quotes/QuoteFiltersDialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

function dedupeQuotesById(list: Quote[]): Quote[] {
  const map = new Map<string, Quote>();
  for (const q of list) {
    if (!q?.id) continue;
    if (!map.has(q.id)) map.set(q.id, q);
  }
  return Array.from(map.values());
}

const statusConfig: Record<QuoteStatus, { label: string; className: string }> = {
  pending: { label: 'Pendente', className: 'bg-amber-100 text-amber-700 hover:bg-amber-100' },
  approved: { label: 'Aprovado', className: 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100' },
  completed: { label: 'Concluído', className: 'bg-blue-100 text-blue-700 hover:bg-blue-100' },
  cancelled: { label: 'Cancelado', className: 'bg-red-100 text-red-700 hover:bg-red-100' },
  rejected: { label: 'Rejeitado', className: 'bg-red-100 text-red-700 hover:bg-red-100' },
};

/**
 * Os status que a barra de botões oferece — e, por consequência, os únicos que
 * `?status=` pode semear. Apontar a URL para um status fora daqui deixaria a
 * tela num recorte sem botão correspondente, sem jeito de o usuário voltar para
 * "Todos" a não ser adivinhando.
 *
 * `rejected` entrou porque o cliente recusando pela página pública é um desfecho
 * que o prestador precisa conseguir listar — antes ele só existia dentro de
 * "Todos". `cancelled` continua de fora: o botão dele seria um sexto rótulo
 * numa barra que já ocupa duas linhas em 360px, e o documento cancelado pelo
 * próprio prestador não é algo que se vá procurar.
 *
 * O RÓTULO é "Rejeitados" e não "Reprovados" de propósito: `Rejeitado` é a
 * palavra que o app inteiro usa (`QUOTE_STATUS_LABELS`, StatusBadge, CSV, XLSX,
 * relatórios) e é a que aparece no selo do próprio card, uma linha abaixo deste
 * botão. Duas palavras para o mesmo status na MESMA tela leem como dois estados
 * diferentes.
 */
const QUOTE_STATUS_FILTERS: QuoteStatus[] = ['pending', 'approved', 'completed', 'rejected'];

/** Os botões da barra, na ordem do ciclo de vida do documento. */
const STATUS_FILTER_OPTIONS: readonly { value: QuoteStatusFilter; label: string }[] = [
  { value: 'all', label: 'Todos' },
  { value: 'pending', label: 'Pendentes' },
  { value: 'approved', label: 'Aprovados' },
  { value: 'completed', label: 'Concluídos' },
  { value: 'rejected', label: 'Rejeitados' },
];

export default function QuotesListPage() {
  const { user } = useAuth();
  const { activeCompany } = useCompany();
  const {
    isPremium,
    quotesCreatedInPeriod,
    freeQuotesLimit,
    incrementQuoteCount,
  } = useSubscription();
  const { isOnline } = useNetworkStatus();
  const navigate = useNavigate();
  // Team roles: members+ may duplicate; only owner/admin may delete.
  const { canWrite, canDelete } = useRole();
  
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  /**
   * `?status=` semeia a aba, e isso não é conveniência: o card de Pendências
   * da home promete "N orçamentos sem resposta" e manda para cá. Sem ler o
   * parâmetro, a tela abria em "Todos" e mostrava uma contagem DIFERENTE da
   * que o card acabou de afirmar — sem erro nenhum, só a impressão de que um
   * dos dois números está errado. Valor desconhecido cai em 'all'.
   */
  const [searchParams] = useSearchParams();
  const [statusFilter, setStatusFilter] = useState<'all' | QuoteStatus>(() => {
    const wanted = searchParams.get('status');
    return wanted && QUOTE_STATUS_FILTERS.includes(wanted as QuoteStatus)
      ? (wanted as QuoteStatus)
      : 'all';
  });
  const [documentFilter, setDocumentFilter] = useState<'quotes' | 'sales'>('quotes');
  const [sortOption, setSortOption] = useState<QuoteSortOption>(DEFAULT_QUOTE_SORT);
  const [periodPreset, setPeriodPreset] = useState<ReportPeriodPreset | null>(null);
  const [valueRange, setValueRange] = useState<QuoteValueRange>(EMPTY_VALUE_RANGE);
  const [filtersOpen, setFiltersOpen] = useState(false);
  /**
   * MODO DE SELEÇÃO — desligado por padrão.
   *
   * A caixa de seleção em todo card cobrava o preço de uma ação em massa de
   * quem só veio consultar a lista: ela roubava largura do título em cada linha
   * (#175) e punha um controle de exceção no caminho do uso comum. Agora ela só
   * existe depois de o usuário dizer que quer selecionar.
   */
  const [selectionMode, setSelectionMode] = useState(false);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkDuplicateOpen, setBulkDuplicateOpen] = useState(false);
  const [bulkDuplicating, setBulkDuplicating] = useState(false);
  const [showPremiumModal, setShowPremiumModal] = useState(false);

  /**
   * BUSCA por cliente, título, ITEM (produto/serviço) e NÚMERO do documento.
   *
   * O índice de itens é carregado só quando há termo digitado — quem nunca
   * busca não paga por ele —, e `complete` diz se ele cobre a empresa inteira
   * ou só o que o cache tinha. A tela MOSTRA essa diferença: uma busca em itens
   * parcial que se apresenta como completa é corte silencioso.
   */
  const itemNames = useQuoteItemNames(activeCompany?.id, quotes, search.trim().length > 0, isOnline);

  /**
   * A faixa do período é resolvida UMA vez por render do recorte, e não dentro
   * do `filter`: `resolvePeriodPreset` cria objetos `Date` a partir de "agora",
   * e chamá-la por documento significaria comparar cada linha contra um "agora"
   * ligeiramente diferente — além de N alocações por render.
   */
  const periodRange = useMemo(
    () => (periodPreset ? resolvePeriodPreset(periodPreset) : null),
    [periodPreset],
  );

  /**
   * O recorte SEM o status — é ele que alimenta as contagens dos botões.
   *
   * Cada botão promete quantos documentos ele traria; contar sobre a lista
   * inteira faria "Pendentes 12" com uma busca ativa que devolve 3, e contar
   * depois do status zeraria todos os outros botões.
   */
  const quotesBeforeStatus = quotes.filter((quote) => {
    const matchesSearch = quoteMatchesSearch(quote, search, itemNames.byQuote.get(quote.id) ?? []);
    const matchesDocument =
      documentFilter === 'sales' ? isSaleDocument(quote) : !isSaleDocument(quote);
    return (
      matchesSearch &&
      matchesDocument &&
      matchesPeriod(quote, periodRange) &&
      matchesValueRange(quote, valueRange)
    );
  });

  const statusCounts = countQuotesByStatus(quotesBeforeStatus);

  const filteredQuotes = sortQuotesBy(
    quotesBeforeStatus.filter(
      (quote) => statusFilter === 'all' || quote.status === statusFilter,
    ),
    sortOption,
  );

  const activeModalFilters = countActiveModalFilters(periodPreset, valueRange);

  /**
   * O VALOR AO VIVO DO HERO. Sai do recorte JÁ FILTRADO (aba + busca + status +
   * período + valor), que é o que torna o número "ao vivo" de verdade: ele
   * responde à mesma pergunta que a lista logo abaixo. A regra de quem entra na
   * soma mora no módulo puro — é dinheiro em `text-3xl` (`quotesListSummary`).
   *
   * `includeDead` liga quando o recorte É um status morto: com o filtro em
   * "Rejeitados", excluir todo mundo faria o hero anunciar R$ 0,00 com a lista
   * cheia embaixo.
   */
  const summary = summarizeQuoteList(filteredQuotes, {
    includeDead: isDeadStatusFilter(statusFilter),
  });

  const {
    selectedCount,
    allSelected,
    isSelected,
    toggleItem,
    clearSelection,
    toggleSelectAll,
    selectedIds,
  } = useBulkSelection(filteredQuotes);

  // Devolução de estoque na exclusão em massa: a consulta só roda com o
  // diálogo aberto e cobre TODOS os selecionados numa leitura só.
  const selectedQuoteIds = useMemo(() => Array.from(selectedIds), [selectedIds]);
  const deletionReturn = useDocumentDeletionReturn('quote', selectedQuoteIds, {
    enabled: bulkDeleteOpen,
  });

  const { exportXlsx } = useXlsxExport();

  // Export the current selection (if any) or all filtered quotes to XLSX.
  // Line items are fetched in a single batched query when online so the export
  // includes the "Itens" sheet; offline we export just the quotes sheet.
  const handleExportXlsx = () => {
    const source =
      selectedCount > 0
        ? filteredQuotes.filter((q) => selectedIds.has(q.id))
        : filteredQuotes;

    exportXlsx(async () => {
      let items: QuoteItemRow[] | undefined;
      const ids = source
        .map((q) => q.id)
        .filter((id) => !String(id).startsWith('offline_'));
      if (isOnline && ids.length > 0) {
        try {
          const { data } = await supabase
            .from('quote_items')
            .select('quote_id, name, quantity, unit, unit_price, discount, total')
            .in('quote_id', ids);
          if (data) items = data as QuoteItemRow[];
        } catch {
          // Non-fatal: fall back to exporting quotes without the items sheet.
        }
      }
      return buildQuotesXlsx(source, items);
    }, 'orcamentos');
  };

  // Load cached data immediately on mount (critical for offline app restart)
  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }
    
    const cached = getCachedQuotes(user.id);
    if (cached !== null) {
      setQuotes(dedupeQuotesById(cached as Quote[]));
    }
    setLoading(false);
  }, [user]);

  // Fetch from server when online. Re-runs on company switch so the list
  // stays scoped to the active company (team accounts).
  useEffect(() => {
    if (!user || !isOnline || !activeCompany) return;
    fetchQuotes();
  }, [user, isOnline, activeCompany?.id]);
    
  // Listen for sync completion to refresh data
  useEffect(() => {
    if (!user) return;
    
    const handleQuotesUpdated = () => {
      if (isOnline) {
        fetchQuotes();
      } else {
        // Reload from cache
        const cached = getCachedQuotes(user.id);
        if (cached) {
          setQuotes(dedupeQuotesById(cached as Quote[]));
        }
      }
    };
    window.addEventListener('quotes-updated', handleQuotesUpdated);
    
    return () => {
      window.removeEventListener('quotes-updated', handleQuotesUpdated);
    };
  }, [user, isOnline]);

  // Clear selection when filters change. A ORDENAÇÃO fica de fora de propósito:
  // ela reorganiza as mesmas linhas, então a seleção continua válida — limpá-la
  // ali faria o usuário perder a marcação só por ter trocado o critério.
  useEffect(() => {
    clearSelection();
  }, [search, statusFilter, documentFilter, periodPreset, valueRange, clearSelection]);

  const fetchQuotes = async () => {
    if (!user || !activeCompany) return;

    try {
      const { data, error } = await supabase
        .from('quotes')
        .select('*')
        .eq('company_id', activeCompany.id)
        .order('created_at', { ascending: false });

      if (data && !error) {
        // Get existing cache to preserve _offline_items and _offline_photos
        const cachedQuotes = getCachedQuotes(user.id) || [];
        
        // Create a map of existing cached data for quick lookup
        const cachedMap = new Map<string, any>();
        cachedQuotes.forEach((q: any) => {
          if (q?.id) cachedMap.set(q.id, q);
        });
        
        // Merge server data with cached nested data (_offline_items, _offline_photos)
        const mergedWithCache = data.map((serverQuote: any) => {
          const cached = cachedMap.get(serverQuote.id);
          if (cached) {
            // Preserve cached items and photos if they exist
            return {
              ...serverQuote,
              _offline_items: cached._offline_items || [],
              _offline_photos: cached._offline_photos || [],
              _offline_pending: false,
            };
          }
          return serverQuote;
        });
        
        // Add offline-pending quotes that haven't synced yet
        const offlinePending = cachedQuotes.filter((q: any) => 
          q._offline_pending === true && 
          String(q.id).startsWith('offline_')
        );
        
        // Server data takes precedence, then add offline-pending items
        const merged = [...mergedWithCache, ...offlinePending] as Quote[];
        const deduped = dedupeQuotesById(merged);
        
        setQuotes(deduped);
        // Cache for offline access - include the merged list with preserved nested data
        cacheQuotes(user.id, deduped);
      }
    } catch (e) {
      console.error('[QuotesListPage] Error fetching quotes:', e);
      // On error, load cached data if available
      const cached = getCachedQuotes(user.id);
      if (cached) {
        setQuotes(dedupeQuotesById(cached as Quote[]));
      }
    }
    setLoading(false);
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  // Check if bulk duplicate is allowed and show modal
  const handleBulkDuplicateRequest = () => {
    if (!canWrite) {
      toast.warning('Você tem acesso somente leitura nesta empresa.', { id: 'readonly-access' });
      return;
    }
    const selectedQuotesArray = filteredQuotes.filter(q => selectedIds.has(q.id));
    const duplicateCount = selectedQuotesArray.filter(
      (quote) => quote.document_type !== 'pedido',
    ).length;

    // PRO users skip confirmation - proceed directly
    if (isPremium) {
      handleBulkDuplicate();
      return;
    }

    // Check quota for free users
    const newTotal = quotesCreatedInPeriod + duplicateCount;
    if (newTotal > freeQuotesLimit) {
      setShowPremiumModal(true);
      return;
    }

    // Show confirmation modal for free users only
    setBulkDuplicateOpen(true);
  };

  // Bulk Duplicate after confirmation
  const handleBulkDuplicate = async () => {
    const selectedQuotesArray = filteredQuotes.filter(q => selectedIds.has(q.id));

    setBulkDuplicating(true);

    try {
      // Get current lifetime_quotes_created once at the start
      const { data: profileData } = await supabase
        .from('profiles')
        .select('lifetime_quotes_created')
        .eq('id', user!.id)
        .maybeSingle();

      let currentCount = profileData?.lifetime_quotes_created ?? 0;

      for (const quote of selectedQuotesArray) {
        // Fetch items for this quote
        const { data: items } = await supabase
          .from('quote_items')
          .select('*')
          .eq('quote_id', quote.id);

        // Next sequence ID for this duplicate
        const nextSequenceId = currentCount + 1;

        // Create duplicated quote with persistent sequence ID
        const { data: newQuote, error: quoteError } = await supabase
          .from('quotes')
          .insert({
            user_id: user!.id,
            client_id: quote.client_id,
            client_name: quote.client_name ? `${quote.client_name} (Cópia)` : 'Sem cliente (Cópia)',
            // O SNAPSHOT de contato tem que vir junto: `client_id` sozinho não
            // basta, porque nada o reconstrói ao abrir o documento — é destas
            // colunas que o PDF imprime telefone e endereço. Sem elas a cópia
            // saía muda, sem erro nenhum na tela.
            client_phone: quote.client_phone ?? null,
            client_address: quote.client_address ?? null,
            client_document: quote.client_document ?? null,
            status: 'pending',
            // A cópia herda o total JÁ CALCULADO da origem, e por isso precisa
            // herdar também as duas parcelas que o compõem — copiar só `total`
            // e deixar `shipping`/`discount` no default 0 faria a cópia abrir
            // com um total que a própria tela não consegue reproduzir.
            total: quote.total,
            shipping: quote.shipping ?? 0,
            discount: quote.discount ?? 0,
            discount_type: quote.discount_type ?? 'value',
            // Mesma lógica para os campos de exibição: a cópia tem que sair
            // igual ao original. `?? null` (e nunca `?? []`) porque uma origem
            // legada precisa continuar legada — senão a cópia de um orçamento
            // antigo perderia o QR PIX que o original tem.
            payment_methods: quote.payment_methods ?? null,
            payment_terms: quote.payment_terms ?? null,
            reference: quote.reference ?? null,
            // A cópia herda o modelo de PDF escolhido no original; `?? null`
            // mantém a cópia de um documento sem escolha seguindo o padrão.
            pdf_template: quote.pdf_template ?? null,
            validity_days: quote.validity_days,
            // As TRÊS colunas da garantia de uma vez. `warranty` sozinho perdia a
            // garantia ESTRUTURADA — que tem precedência sobre o texto livre
            // (#104), então a cópia de um orçamento salvo pelo formulário novo
            // saía sem garantia nenhuma. A chamada fica DENTRO do literal de
            // propósito: extraí-la para uma variável desligaria o guard de
            // varredura (#75).
            ...buildQuoteWarrantyColumns(quoteWarrantyFromRow(quote)),
            // Mesma disciplina: a copia herda o cabecalho da origem. Chamada
            // repetida de proposito — extrair para variavel desliga o guard (#75).
            ...buildQuoteHeadingColumns(quoteHeadingFromRow(quote)),
            ...buildQuoteExtraFieldsColumns({ extraFields: quoteExtraFieldsFromRow(quote) }),
            notes: quote.notes,
            terms_conditions: quote.terms_conditions,
            document_type: quote.document_type,
            title: quote.title ? `${quote.title} (Cópia)` : null,
            sequence_id: nextSequenceId,
            show_sequence_id: quote.show_sequence_id,
            company_id: activeCompany?.id ?? null,
          })
          .select()
          .single();

        if (quoteError || !newQuote) {
          console.error('Error duplicating quote:', quoteError);
          continue;
        }

        // Duplicate items
        if (items && items.length > 0) {
          const duplicatedItems = items.map(item => ({
            ...buildQuoteItemInsert(item, item.position, newQuote.id),
            // A cópia herda a COBRANÇA da despesa cobrável, não o VÍNCULO: dois
            // orçamentos apontando para o mesmo lançamento fariam uma edição de
            // valor recalcular o total do documento errado.
            finance_entry_id: null,
          }));

          await supabase.from('quote_items').insert(duplicatedItems);
        }

        await incrementQuoteCount({
          countTowardQuota: !isPremium && quote.document_type !== 'pedido',
          sequenceId: nextSequenceId,
        });
        currentCount++; // Keep our local counter in sync for the next sequence ID
      }

      toast.success(`${selectedQuotesArray.length} orçamento(s) duplicado(s)!`);
      setBulkDuplicateOpen(false);
      clearSelection();
      fetchQuotes();
    } catch (error) {
      console.error('Error in bulk duplicate:', error);
      toast.error('Erro ao duplicar orçamentos');
    } finally {
      setBulkDuplicating(false);
    }
  };

  const handleBulkDelete = async () => {
    if (!canDelete) {
      toast.warning('Apenas o proprietário e administradores podem excluir.');
      return;
    }
    setBulkDeleting(true);

    const idsToDelete = Array.from(selectedIds);

    // Calcular → apagar → devolver (`src/lib/documentDeletion.ts`). Os itens
    // não são mais apagados aqui: são CASCADE, e apagá-los antes de saber se o
    // orçamento pode morrer era o que deixava documentos sem nenhuma linha.
    let outcome: DocumentDeletionOutcome<QuoteDeletionCounts>;
    try {
      outcome = await runDocumentDeletion({
        prepareReturn: () => deletionReturn.prepare(),
        remove: () => deleteQuotesCascade(idsToDelete),
      });
    } catch (e) {
      console.error('Error deleting quotes:', e);
      toast.error('Erro ao excluir orçamentos');
      return;
    } finally {
      setBulkDeleting(false);
    }

    if (outcome.prepareFailed || outcome.returnFailed) {
      toast.warning('Os orçamentos foram excluídos, mas não foi possível devolver o estoque.');
    } else if (outcome.returned) {
      toast.success(
        `${plural(outcome.returned.products, 'produto devolvido', 'produtos devolvidos')} ao estoque.`,
      );
    }

    const releaseMessage = billableReleaseMessage(outcome.result.released);
    if (releaseMessage) toast.info(releaseMessage);

    toast.success(`${plural(outcome.result.deleted, 'orçamento excluído', 'orçamentos excluídos')}!`);
    setBulkDeleteOpen(false);
    clearSelection();
    fetchQuotes();
  };

  if (!user) {
    return (
      <MainLayout showFab={false}>
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <FileText className="mb-4 h-16 w-16 text-muted-foreground/50" />
          <h2 className="text-xl font-semibold">Orçamentos</h2>
          <p className="mb-4 text-muted-foreground">
            Faça login para ver seus orçamentos
          </p>
          <Button3D onClick={() => navigate('/auth')}>Fazer login</Button3D>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout hideFab={selectedCount > 0}>
      <div className="pb-4">
        {/* ================================================================== */}
        {/* HERO — o desenho oficial do app (o mesmo de /quotes/new)            */}
        {/* ------------------------------------------------------------------ */}
        {/* Sétima tela no molde, e a primeira que é uma LISTA. O que precisou
            ser traduzido (o mesmo exercício do #182 na home):

            · O EYEBROW diz o "onde" — nas telas de formulário ele é a ação
              ("Novo documento"), aqui é a família de que a lista faz parte.
            · O H1 acompanha a ABA, e não é duplicação da faixa de abas: é o
              hero afirmando de qual conjunto o número logo abaixo saiu. Um
              cabeçalho fixo em "Orçamentos" com um total de VENDAS embaixo
              seria a mentira que o #182 descreve, uma linha acima do número.
            · O VALOR AO VIVO é a soma do recorte visível (aba + busca +
              status). A regra de quem entra nela é de NEGÓCIO e mora no módulo
              puro `quotesListSummary`.
            · As pílulas de status do /quotes/new NÃO têm análogo aqui: lá elas
              DEFINEM o status do documento, aqui o status é filtro — e filtro
              é alternador de aba, que segue azul e herdado do componente base
              (#25/#160). Pintá-las de branco sobre o verde faria um filtro
              parecer uma ação. */}
        <div className="relative -mx-4 -mt-6 overflow-hidden rounded-b-[2rem] bg-gradient-to-br from-primary via-primary to-emerald-600 px-4 pb-14 pt-6 text-white shadow-[0_10px_30px_-12px_hsla(var(--primary)/0.6)]">
          <div className="pointer-events-none absolute -right-10 -top-16 h-44 w-44 rounded-full bg-white/10" />
          <div className="pointer-events-none absolute -left-16 top-16 h-40 w-40 rounded-full bg-white/5" />

          {/* Sem botão de voltar: `/quotes` é destino da navegação inferior,
              como a home — não se "volta" de uma aba raiz. */}
          <div className="relative flex items-start gap-2">
            <div className="min-w-0 flex-1">
              <p className="text-[11px] uppercase tracking-wide text-white/70">Documentos</p>
              <h1 className="line-clamp-2 text-lg font-bold leading-tight">
                {documentFilter === 'sales' ? 'Vendas' : 'Orçamentos'}
              </h1>
            </div>
            {/* Ação SECUNDÁRIA vira ícone (#186): as duas com texto disputariam
                a linha com o título, e sem `flex-wrap` o excedente não quebra —
                ele sai da tela. `aria-label`/`title` são obrigatórios aqui,
                senão o botão perde o nome acessível junto com o texto (#142) —
                e a contagem da seleção vai neles, porque para o VIEWER (que não
                recebe a BulkActionBar) este era o único lugar da tela que dizia
                quantos estavam selecionados. */}
            <Button
              variant="ghost"
              size="icon"
              onClick={handleExportXlsx}
              disabled={filteredQuotes.length === 0}
              className="relative h-9 w-9 shrink-0 rounded-full bg-white/15 text-white hover:bg-white/25 hover:text-white disabled:opacity-40"
              aria-label={
                selectedCount > 0
                  ? `Exportar ${plural(selectedCount, 'selecionado', 'selecionados')}`
                  : 'Exportar'
              }
              title={selectedCount > 0 ? `Exportar (${selectedCount})` : 'Exportar'}
            >
              <FileSpreadsheet className="h-4 w-4" />
              {selectedCount > 0 && (
                <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-white px-1 text-[10px] font-bold leading-none text-emerald-800">
                  {selectedCount}
                </span>
              )}
            </Button>
            {canWrite && (
              /* A ação PRIMÁRIA mantém o texto — é ela que o excedente comeria
                 primeiro se tudo fosse texto, e é a que menos pode sumir. */
              <Button3D
                size="sm"
                color="light"
                className="shrink-0"
                onClick={() => navigate('/quotes/new')}
              >
                <Plus className="mr-1.5 h-4 w-4" />
                Novo
              </Button3D>
            )}
          </div>

          <div className="relative mt-5 min-w-0">
            <p className="text-xs text-white/75">
              {quoteListTotalLabel(documentFilter, statusFilter)}
            </p>
            {/* `heroTotalSizeClass` é a FONTE ÚNICA do degrau de tamanho
                (#159-b): aqui o valor tem a largura inteira do hero e sobra
                folga, mas escrever uma regra local seria a quinta cópia de uma
                medida que já divergiu entre telas uma vez. */}
            <p
              className={cn(
                'truncate font-bold leading-tight',
                heroTotalSizeClass(formatCurrency(summary.total)),
              )}
            >
              {formatCurrency(summary.total)}
            </p>
            <p className="mt-0.5 text-xs text-white/70">
              {summary.count === 0
                ? 'Nenhum documento neste filtro'
                : plural(
                    summary.count,
                    documentFilter === 'sales' ? 'venda' : 'orçamento',
                    documentFilter === 'sales' ? 'vendas' : 'orçamentos',
                  )}
              {/* "no silent caps": o que ficou fora da soma é DITO. Sem isto o
                  total apareceria menor do que a lista sugere, sem explicação. */}
              {summary.excludedCount > 0 &&
                ` · ${plural(
                  summary.excludedCount,
                  'cancelado fora da conta',
                  'cancelados fora da conta',
                )}`}
            </p>
          </div>
        </div>

        {/* Conteúdo — sobe e encosta na curva do hero, como em /quotes/new. */}
        <div className="relative z-10 -mt-9 space-y-5">
        {/* Tipo de documento: vendas rápidas não poluem a lista padrão. */}
        <Tabs value={documentFilter} onValueChange={(v) => setDocumentFilter(v as typeof documentFilter)}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="quotes">Orçamentos</TabsTrigger>
            <TabsTrigger value="sales">Vendas</TabsTrigger>
          </TabsList>
        </Tabs>

        {/* BLOCO DE CONTROLES DA LISTA — busca, status e seleção.
            Eles são um grupo só (todos respondem "o que eu estou vendo"), então
            ficam juntos com `space-y-2` em vez do `space-y-5` que separa as
            SEÇÕES da tela. Com o espaçamento de seção entre eles, os três
            pareciam três blocos independentes e empurravam o primeiro card para
            baixo da dobra. */}
        <div className="space-y-2">

        {/* Busca + ordenação + filtros. Os dois ícones são `shrink-0` e o campo
            é `min-w-0 flex-1`: sem isso o input empurraria os botões para fora
            da tela em vez de encolher (é o #186, e o excedente sai pela direita
            sem quebrar linha). */}
        <div className="space-y-1.5">
          <div className="flex items-center gap-2">
            <div className="relative min-w-0 flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Cliente, produto ou #0001"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Ordenação: menu, não modal — é uma escolha única entre seis
                rótulos curtos, e um diálogo para isso seria dois cliques a mais
                pelo mesmo resultado. */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-10 w-10 shrink-0 rounded-xl"
                  aria-label={`Ordenar (${QUOTE_SORT_LABELS[sortOption]})`}
                  title={`Ordenar: ${QUOTE_SORT_LABELS[sortOption]}`}
                >
                  <ArrowUpDown className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {QUOTE_SORT_ORDER.map((option) => (
                  <DropdownMenuItem
                    key={option}
                    onSelect={() => setSortOption(option)}
                    className="gap-2"
                  >
                    {/* O CHECK ocupa o mesmo espaço quando ausente: sem o
                        placeholder, o rótulo escolhido fica deslocado dos
                        outros e a lista parece desalinhada. */}
                    <span className="flex h-4 w-4 items-center justify-center">
                      {sortOption === option && <Check className="h-4 w-4 text-primary" />}
                    </span>
                    {QUOTE_SORT_LABELS[option]}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Filtros: aqui SIM um modal — são três controles independentes que
                fazem sentido escolhidos juntos, e o rascunho do diálogo é o que
                impede a lista de se reorganizar a cada tecla do campo de valor. */}
            <Button
              variant="outline"
              size="icon"
              className="relative h-10 w-10 shrink-0 rounded-xl"
              onClick={() => setFiltersOpen(true)}
              aria-label={
                activeModalFilters > 0
                  ? `Filtros (${plural(activeModalFilters, 'ativo', 'ativos')})`
                  : 'Filtros'
              }
              title="Filtros"
            >
              <SlidersHorizontal className="h-4 w-4" />
              {activeModalFilters > 0 && (
                <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-bold leading-none text-white">
                  {activeModalFilters}
                </span>
              )}
            </Button>
          </div>

          {/* A busca em ITENS depende de um índice que pode estar incompleto
              (cache do warmup ainda parcial, ou leitura que falhou). Dizer isso
              é obrigatório: sem o aviso, "2 resultados" lê como "só existem 2",
              e o usuário conclui que o produto não está em lugar nenhum. */}
          {search.trim().length > 0 && !itemNames.complete && (
            <p className="px-1 text-xs text-muted-foreground">
              {itemNames.loading
                ? 'Procurando também nos itens dos documentos…'
                : 'A busca por produto ou serviço está usando só o que está salvo no aparelho.'}
            </p>
          )}
        </div>

        {/* STATUS — botões planos, não alternador.
            O estilo de pílula 3D azul (#25) ficou reservado à barra
            Orçamentos/Vendas, que é a única troca de CONTEXTO da tela; o status
            é um recorte, e recorte não merece o peso visual de um alternador.

            ── UMA LINHA, NENHUM RÓTULO CORTADO: o arranjo foi MEDIDO ──
            Em 360px sobram 328 úteis, e os cinco rótulos custam (medido, com a
            fonte real): `text-sm` + contagem = 530px · `text-xs` + contagem =
            420 · `text-xs` sem contagem = 359 · `text-[11px]` com `px-1.5` sem
            contagem = **319**. Ou seja, só o último cabe — e a CONTAGEM é o que
            não cabia, não o texto. Ela não se perdeu: vai no `title`/`aria-label`
            de cada botão, onde não custa pixel nenhum.
            `whitespace-nowrap` + `shrink-0` são o que garantem o enunciado: sem
            eles o flex encolheria os itens e o rótulo quebraria ou sumiria —
            e uma grade de 5 colunas iguais cortaria "Concluídos" para caber na
            fatia do "Todos" (#162).

            `overflow-x-auto` é REDE DE SEGURANÇA, não o desenho: com os cinco
            cabendo, ele nunca rola. Ele existe porque a folga é de poucos pixels
            e uma métrica de fonte diferente (outro device, outra versão da
            fonte) empurraria o último botão para FORA da viewport — que é
            exatamente o #185, e é um estrago maior do que uma barra que rola.
            Mesmo padrão já usado em /relatorios e /modelos-mensagens. */}
        <div className="-mx-1 flex gap-1 overflow-x-auto px-1 pb-0.5">
          {STATUS_FILTER_OPTIONS.map((option) => {
            const active = statusFilter === option.value;
            // A contagem responde "quantos eu veria se clicasse aqui" — por
            // isso sai do recorte SEM o status (busca, período e faixa de valor
            // já aplicados). Um número que não bate com a lista depois do
            // clique é pior do que número nenhum.
            const count = statusCounts[option.value] ?? 0;
            return (
              <button
                key={option.value}
                type="button"
                onClick={() => setStatusFilter(option.value)}
                aria-pressed={active}
                aria-label={`${option.label} (${count})`}
                title={`${option.label}: ${plural(count, 'documento', 'documentos')}`}
                className={cn(
                  'shrink-0 whitespace-nowrap rounded-full border px-1.5 py-0.5 text-[11px] font-medium leading-5 transition-colors',
                  active ? FILTER_CHIP_ON : FILTER_CHIP_OFF,
                )}
              >
                {option.label}
              </button>
            );
          })}
        </div>

        {/* Entrada e saída do MODO DE SELEÇÃO.
            Fora dele a esquerda mostra a ORDENAÇÃO ativa e a direita o botão
            "Selecionar"; dentro, a ordenação dá lugar à marcação de todos e o
            botão vira a SAÍDA. Sair limpa a seleção: deixar itens marcados sem
            nenhuma caixa na tela deixaria a `BulkActionBar` falando de uma
            escolha invisível.

            A ordenação ocupa esse lugar porque ele estava VAZIO e porque ela é
            a única decisão da tela que não se anuncia sozinha: status tem
            botões, período e faixa de valor têm o selo do ícone de filtro, mas
            "por que este documento está no topo?" só se responde abrindo o
            menu. É rótulo, não botão — o controle é o ícone logo acima, e um
            segundo gatilho para a mesma coisa dividiria o alvo do toque. */}
        {!loading && filteredQuotes.length > 0 && (
          <div className="flex items-center justify-between gap-2 px-1">
            {selectionMode ? (
              /* `<label>` em volta do Checkbox do Radix alterna DUAS vezes e
                 vira no-op (#29) — o padrão do app é o par Checkbox + área de
                 texto com `onClick` próprio. */
              <div className="flex items-center gap-2">
                <Checkbox
                  checked={allSelected}
                  onCheckedChange={(checked) => toggleSelectAll(!!checked, filteredQuotes)}
                  aria-label="Selecionar todos os orçamentos"
                />
                <span
                  className="cursor-pointer text-sm text-muted-foreground"
                  onClick={() => toggleSelectAll(!allSelected, filteredQuotes)}
                >
                  Selecionar todos ({filteredQuotes.length})
                </span>
              </div>
            ) : (
              <span className="flex min-w-0 items-center gap-1.5 text-sm text-muted-foreground">
                <ArrowUpDown className="h-3.5 w-3.5 shrink-0" />
                <span className="truncate">{QUOTE_SORT_LABELS[sortOption]}</span>
              </span>
            )}
            <button
              type="button"
              onClick={() => {
                if (selectionMode) clearSelection();
                setSelectionMode((prev) => !prev);
              }}
              className="shrink-0 rounded-full px-2 py-1 text-sm font-medium text-primary transition-colors hover:bg-primary/10"
            >
              {selectionMode ? 'Concluir' : 'Selecionar'}
            </button>
          </div>
        )}

        {/* Quotes List — DENTRO do mesmo grupo `space-y-2` da busca/status/
            seleção, e não um `-mt-N` competindo com o `space-y-5` de fora.
            `space-y-*` do Tailwind mira `> :not(:first-child)` (dois níveis de
            seletor); uma margem negativa num FILHO DIRETO do container externo
            tem especificidade menor e PERDE a briga — foi medido: `-mt-3`
            aplicado ali resultava em `margin-top: 20px` computado, a classe
            simplesmente não pegava. Aninhar em vez de negativar é o que garante
            a mesma distância (8px) sem depender de quem vence no CSS. */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : filteredQuotes.length > 0 ? (
          /* No DESKTOP a lista vira duas colunas: o card já é uma linha densa
             (identificação à esquerda, valor e status à direita), então esticá-lo
             por 1000px deixaria um vão enorme no meio e mostraria menos
             documentos por tela — o oposto do que quem opera em PC quer.

             `grid-cols-1` explícito no celular: sem ele a coluna implícita é
             `auto`, cujo mínimo é o min-content do item mais largo, e um título
             sem espaços estica a coluna e vaza a página (#185). O `gap-3`
             reproduz exatamente a distância do `space-y-3` anterior. */
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-2 2xl:grid-cols-3">
            {filteredQuotes.map((quote) => {
              const config = statusConfig[quote.status];
              const selected = isSelected(quote.id);
              return (
                <div
                  key={quote.id}
                  className={cn(
                    CONTENT_CARD,
                    'flex cursor-pointer items-center gap-3 p-4 transition-shadow hover:shadow-md',
                    selected && 'ring-2 ring-primary',
                  )}
                  onClick={() => navigate(`/quotes/${quote.id}`)}
                >
                  {/* Coluna da ESQUERDA: identificação + conteúdo. Ela é quem
                      dita a altura do card — o `items-center` do card faz o
                      bloco preço+status centrar contra ELA, e não contra a
                      linha do título. */}
                  <div className="min-w-0 flex-1 space-y-2">
                    {/* LINHA DE IDENTIFICAÇÃO: seleção + número + data + selos.
                        Tudo que identifica o documento (mas não é o conteúdo
                        dele) mora aqui, o que devolve a largura inteira ao
                        título na linha de baixo. */}
                    <div className="flex flex-wrap items-center gap-2">
                      {/* A caixa só existe no MODO DE SELEÇÃO: fora dele ela
                          ocupava espaço em toda linha por causa de uma ação que
                          é exceção. */}
                      {selectionMode && (
                        <Checkbox
                          checked={selected}
                          onCheckedChange={() => toggleItem(quote.id)}
                          // O card inteiro navega; o clique na seleção não pode
                          // subir, senão marcar um item abriria o documento.
                          onClick={(e) => e.stopPropagation()}
                          aria-label={`Selecionar orçamento de ${quote.client_name || 'sem cliente'}`}
                        />
                      )}
                      {/* Offline pending indicator */}
                      {(quote as any)._offline_pending && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div className="flex items-center gap-1 text-amber-600">
                              <Clock className="h-3.5 w-3.5" />
                            </div>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Aguardando sincronização</p>
                          </TooltipContent>
                        </Tooltip>
                      )}
                      {quote.sequence_id && (
                        <Badge variant="outline" className="font-mono text-xs px-1.5 py-0 h-5 border-primary/30 text-primary bg-primary/5">
                          #{String(quote.sequence_id).padStart(4, '0')}
                        </Badge>
                      )}
                      {/* `created_at` é TIMESTAMP: formatar pelo calendário
                          LOCAL (date-fns sobre `new Date`) é o certo — cortar a
                          string no 'T' daria a data em UTC e imprimiria o DIA
                          SEGUINTE para tudo criado depois das 21h (#114). */}
                      <Badge variant="outline" className="h-5 px-1.5 py-0 text-xs font-normal text-muted-foreground">
                        {format(new Date(quote.created_at), 'dd/MM/yyyy', { locale: ptBR })}
                      </Badge>
                      {isSaleDocument(quote) && (
                        <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">
                          Venda
                        </Badge>
                      )}
                      {/* Viewed indicator for PRO users */}
                      {isPremium && quote.viewed_at && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div className="flex items-center gap-1 text-primary">
                              <Eye className="h-3.5 w-3.5" />
                            </div>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Visualizado em {format(new Date(quote.viewed_at), "dd/MM 'às' HH:mm", { locale: ptBR })}</p>
                          </TooltipContent>
                        </Tooltip>
                      )}
                    </div>

                    {/* `min-w-0` não é decoração (#175): sem ele um flex item
                        nasce com `min-width: auto` e um título sem espaços
                        estica a linha para fora da viewport. */}
                    <div className="min-w-0 space-y-0.5">
                        {/* Sem clamp e com `break-words`: é o título que
                            distingue duas linhas da mesma lista, e cortá-lo
                            come justamente o que informa (#175). */}
                        {quote.title ? (
                          <p className="min-w-0 break-words font-bold">{quote.title}</p>
                        ) : (
                          <p className="min-w-0 break-words font-medium">
                            {isSaleDocument(quote)
                              ? quote.client_name || 'Venda rápida'
                              : quote.client_name || 'Sem cliente'}
                          </p>
                        )}
                        {/* Sem `truncate` também aqui: quando o título é
                            genérico ("Orçamento"), é o nome do cliente que
                            distingue duas linhas — cortá-lo devolve a lista de
                            itens idênticos que o #175 veio matar. A linha
                            quebra e o card cresce, que é o custo certo. */}
                    {quote.title && (
                      <p className="break-words text-sm text-muted-foreground">
                        {quote.client_name || 'Sem cliente'}
                      </p>
                    )}
                    </div>
                  </div>

                  {/* Preço + status são UM CONJUNTO, e por isso um bloco só:
                      centrados entre si no eixo horizontal (à direita, cada um
                      ficava pendurado numa borda diferente, porque o selo é mais
                      largo que o valor) e centrados na ALTURA DO CARD pelo
                      `items-center` do card — não na linha do título, que muda
                      de altura conforme o nome quebra em 1, 2 ou 3 linhas. */}
                  <div className="flex shrink-0 flex-col items-center gap-1">
                    <p className="font-semibold">{formatCurrency(quote.total)}</p>
                    <Badge className={config.className}>
                      {config.label}
                    </Badge>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className={cn(CONTENT_CARD, 'flex flex-col items-center justify-center border-dashed py-8 text-center')}>
            <FileText className="mb-3 h-12 w-12 text-muted-foreground/50" />
            <p className="text-muted-foreground">
              {search || statusFilter !== 'all' ? 'Nenhum orçamento encontrado' : 'Nenhum orçamento ainda'}
            </p>
          </div>
        )}
        </div>
      </div>
      </div>

      {/* Bulk Action Bar — shown to members+ (bulk duplicate); bulk delete
          only for owner/admin. Viewers: hidden (selection stays for export). */}
      {canWrite && (
        <BulkActionBar
          selectedCount={selectedCount}
          totalCount={filteredQuotes.length}
          allSelected={allSelected}
          onSelectAll={(checked) => toggleSelectAll(checked, filteredQuotes)}
          onDelete={canDelete ? () => setBulkDeleteOpen(true) : undefined}
          onClear={clearSelection}
          isDeleting={bulkDeleting}
          onDuplicate={handleBulkDuplicateRequest}
          isDuplicating={bulkDuplicating}
        />
      )}

      {/* Bulk Delete Dialog */}
      <BulkDeleteDialog
        open={bulkDeleteOpen}
        onOpenChange={setBulkDeleteOpen}
        count={selectedCount}
        itemType={selectedCount === 1 ? 'orçamento' : 'orçamentos'}
        onConfirm={handleBulkDelete}
        isDeleting={bulkDeleting}
        extra={<StockReturnOnDeleteNotice state={deletionReturn} />}
      />

      {/* Bulk Duplicate Dialog */}
      <BulkDuplicateDialog
        open={bulkDuplicateOpen}
        onOpenChange={setBulkDuplicateOpen}
        count={selectedCount}
        onConfirm={handleBulkDuplicate}
        isDuplicating={bulkDuplicating}
        remainingCredits={isPremium ? undefined : freeQuotesLimit - quotesCreatedInPeriod}
      />

      {/* Filtros: status, período e faixa de valor.
          O STATUS aparece nos dois lugares (barra e modal) de propósito, e os
          dois escrevem no MESMO estado — é o que impede o modal de dizer uma
          coisa e a barra outra. Deixá-lo só no modal esconderia o recorte mais
          usado atrás de dois cliques; deixá-lo só na barra faria o modal de
          "filtros" não filtrar a coisa que o usuário mais filtra. */}
      <QuoteFiltersDialog
        open={filtersOpen}
        onOpenChange={setFiltersOpen}
        statusOptions={STATUS_FILTER_OPTIONS}
        status={statusFilter}
        period={periodPreset}
        value={valueRange}
        onApply={(next) => {
          setStatusFilter(next.status);
          setPeriodPreset(next.period);
          setValueRange(next.value);
        }}
      />

      {/* Premium Modal for quota exceeded */}
      <PremiumModal
        open={showPremiumModal} 
        onOpenChange={setShowPremiumModal}
        reason="limit"
      />
    </MainLayout>
  );
}
